import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { message } from "antd";
import { AppContext } from "App";
import LottieAnimation from "components/basic/Spinner/Lottie";
import BankAccountMode from "components/modals/Payment/BankAccountMode";
import { CategoryTitle } from "enums/questionnaire";
import { useBankConnection } from "hooks/bank/useBankConnection";
import { useUser } from "hooks/common/useUser";
import { useLocalStorage } from "hooks/helpers/useLocalStorage";
import { useDispatch } from "react-redux";
import { useAppSelector } from "redux/hooks";
import { setAnswers } from "redux/reducers/answers";
import { setBankAccounts } from "redux/reducers/bankAccounts";
import { next, selectCurrentNavigation } from "redux/reducers/navigation";
import { useDeleteClientBankAccountMutation } from "services/banking";
import { useCompleteConnectionMutation } from "services/connectBankAccount";
import { IUser } from "types/auth";
import { logGoogleAnalyticsEvent } from "utils/analytics";
import { logError, logMessage } from "utils/sentry";
import { extractCountryCodeAndRefParams } from "utils/urlUtils";

import BankAccountCards from "../BankAccountCards";
import ConnectPlaidModal from "../OpenBanking/ConnectPlaidModal";

type BankAccountProps = {
    user: IUser;
    handleContinue: () => void;
    isHeaderComponent: boolean;
    refetchConnectedBankAccounts: () => any;
};

const BankAccount: FC<BankAccountProps> = ({ handleContinue, isHeaderComponent, refetchConnectedBankAccounts }) => {
    const dispatch = useDispatch();

    const { user } = useUser();
    const { isMobile } = useContext(AppContext);

    const { currentStep } = useAppSelector(selectCurrentNavigation);
    const { connectedBankAccounts } = useAppSelector(state => state.bankAccounts);
    const { answers } = useAppSelector(state => state.answers);

    const [deleteClientBankAccount] = useDeleteClientBankAccountMutation();
    const [completeConnection] = useCompleteConnectionMutation();

    const [initialBankCount, setInitialBankCount] = useLocalStorage<number>("number_bank_accounts", 0);
    const [spinnerCompleted, setSpinnerCompleted] = useState(false);

    const [paymentModeModal, setPaymentModeModal] = useState<boolean>(false);

    const refParams = useMemo(() => {
        const [, refParams] = extractCountryCodeAndRefParams(window.location.href);
        return refParams;
    }, []);

    const {
        linkToken,
        isModalOpen,
        bankConnecting,
        setIsModalOpen,
        openConnectModal,
        setBankConnecting,
    } = useBankConnection({
        user: user!,
        connectedBankAccountsLength: connectedBankAccounts.length,
        setInitialBankCount,
    });

    const handleDeleteBankAccount = async (index: number) => {
        const accountId = connectedBankAccounts[index]?._id;
        if (accountId) {
            try {
                await deleteClientBankAccount({ accountId }).unwrap();
                dispatch(setBankAccounts(connectedBankAccounts.filter((_, i) => i !== index)));
                dispatch(setAnswers({ ...answers, [CategoryTitle.CIS]: {}, [CategoryTitle.SelfEmployment]: {}, [CategoryTitle.Employment]: {}, [CategoryTitle.InterestIncome]: {}, [CategoryTitle.Dividends]: {}, [CategoryTitle.Rental]: {} }));
                message.success("Bank account deleted successfully");
            } catch (error) {
                console.error("Failed to delete bank account: ", error);
                message.error("Failed to delete bank account");
            }
        }
    };

    const closeConnectModal = useCallback(() => {
        setIsModalOpen(false);

        let attempts = 0;
        const maxAttempts = 120;

        const checkBankAccounts = async () => {
            const refreshedData = await refetchConnectedBankAccounts().unwrap();
            const updatedBankCount = refreshedData?.accDetails?.length || 0;
            if (updatedBankCount > initialBankCount) {
                return;
            }
            if (attempts >= maxAttempts) {
                return;
            }
            attempts++;
            setTimeout(checkBankAccounts, 5000); // Retry every 5 seconds
        };

        checkBankAccounts();

        dispatch(next({
            nextCategory: 1,
            nextStep: 0,
            nextQuestion: 0,
        }));
    }, [dispatch, refetchConnectedBankAccounts, initialBankCount, setIsModalOpen]);

    const pollConnectedBankAccounts = useCallback(async (retryCount = 0) => {
        try {
            const result = await refetchConnectedBankAccounts().unwrap();
            if (result) {
                logMessage("Connected bank accounts fetched successfully.");
                return;
            }
        } catch (error) {
            const contextInfo = {
                clientId: user?.id,
                function: "refetchConnectedBankAccounts",
                error: "Refreshing connected bank accounts failed.",
                attempt: retryCount + 1,
            };
            logError(error, contextInfo);
        }

        // Retry up to 5 times
        if (retryCount < 4) {
            setTimeout(() => pollConnectedBankAccounts(retryCount + 1), 1500);
        } else {
            logMessage(`Max retries reached for fetching connected bank accounts for client ${user?.id}.`);
        }
    }, [refetchConnectedBankAccounts, user?.id]);

    useEffect(() => {
        if (refParams) {
            if (currentStep === 0) {
                dispatch(next({
                    nextCategory: 0,
                    nextStep: 1,
                    nextQuestion: 0,
                }));
            }
            setBankConnecting(true);

            const maxSpinnerDuration = setTimeout(() => {
                setBankConnecting(false);
                setSpinnerCompleted(true);
            }, 4000);

            completeConnection({ ref: refParams, bankDetailsOnly: false })
                .catch(error => {
                    const contextInfo = {
                        clientId: user?.id,
                        function: "completeConnection",
                        error: error.message,
                    };
                    logError(error, contextInfo);
                })
                .finally(() => {
                    clearTimeout(maxSpinnerDuration);

                    const url = new URL(window.location.href);
                    url.searchParams.delete("ref");
                    window.history.replaceState(null, "", url.toString());

                    setTimeout(() => {
                        refetchConnectedBankAccounts();
                    }, 500);

                    setBankConnecting(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refParams]);

    useEffect(() => {
        if (spinnerCompleted) {
            pollConnectedBankAccounts();
            setSpinnerCompleted(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spinnerCompleted]);

    return (
        <div className={"new-questionnaire__bank-account mt10"}>
            <LottieAnimation isLoading={bankConnecting} backgroundColor={"white"}/>

            <div className="new-questionnaire__bank-account-title">
                Connected Bank Accounts
            </div>
            <div className="mt12">
                <BankAccountCards accounts={connectedBankAccounts || []} handleDeleteBankAccount={handleDeleteBankAccount} />
            </div>

            <div className="new-questionnaire__bank-accounts-text2 mt20">
                Is there {connectedBankAccounts.length ? "another" : "any"} bank account you use for your business finances?
            </div>
            {isHeaderComponent && <div className="new-questionnaire__warning-text taCenter">⚠️ Connect all the accounts where you receive your salary and manage your business expenses</div>}
            <div className="new-questionnaire__bank-accounts-buttons mt30">
                {!isHeaderComponent && (
                    <div
                        className="shared__secondary-button w100"
                        onClick={() => {
                            logGoogleAnalyticsEvent("bank_account_continue_clicked", {
                                has_connected_accounts: connectedBankAccounts.length > 0,
                                screen: isHeaderComponent ? "header_bank_account" : "bank_account",
                                platform: isMobile ? "mobile" : "desktop",
                            });
                            handleContinue();
                        }}
                    >
                        No, Continue
                    </div>
                )}
                <div
                    className={"shared__primary-button w100"}
                    onClick={() => {
                        logGoogleAnalyticsEvent("bank_account_connect_clicked", {
                            has_connected_accounts: connectedBankAccounts.length > 0,
                            screen: isHeaderComponent ? "header_bank_account" : "bank_account",
                            platform: isMobile ? "mobile" : "desktop",
                        });
                        openConnectModal();
                    }}
                >
                    {connectedBankAccounts?.length ? "Yes, Add More" : "Connect Account"}
                </div>
            </div>
            <BankAccountMode
                paymentModeModal={paymentModeModal}
                setPaymentModeModal={setPaymentModeModal}
            />
            {linkToken && isModalOpen && <ConnectPlaidModal
                linkToken={linkToken}
                onSuccess={() => {
                    closeConnectModal();
                }
                }
                onExit={() => {
                    setIsModalOpen(false);
                }
                }/>
            }
        </div>
    );
};

export default BankAccount;
